import { type UseQueryResult, useMutation, useQuery, useQueryClient } from "@tanstack/solid-query";
import {
	type Accessor,
	createContext,
	createEffect,
	createMemo,
	createSignal,
	useContext
} from "solid-js";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { getPublicMessageFromError } from "~/solidJs/shared";
import {
	type CalculateUpgradeCostResponse,
	MemberRole,
	SubscriptionAction,
	TeamApi,
	type TeamInfoResponse,
	type TeamMember,
	UpgradeAction,
	type UpgradeItem,
	UpgradeWhen
} from "~/solidJs/shared/api/TeamApi";
import {
	showToastMessage,
	ToastMessageTypes
} from "~/solidJs/widgets/WorkspaceToast/WorkspaceToast";
import { closeUpdateSeatsModal } from "./updateSeatsModal";

export interface UpdateSeatsContextValue {
	// State
	selectedSeats: Accessor<Set<number>>;
	teamQuery: UseQueryResult<TeamInfoResponse | null>;
	calculationQuery: UseQueryResult<CalculateUpgradeCostResponse>;

	// Computed
	teamMembers: Accessor<TeamMember[]>;
	totalSeats: Accessor<number>;
	usedSeats: Accessor<number>;
	unusedSeatsCount: Accessor<number>;
	selectedCount: Accessor<number>;
	newTotalPrice: Accessor<number | null>;
	totalCostChange: Accessor<number | null>;
	isCalculating: Accessor<boolean>;

	// Methods
	handleCheckboxChange: (memberId: number) => void;
	isOwner: (member: TeamMember) => boolean;
	handleConfirmRemoval: () => Promise<void>;
	isRemoving: Accessor<boolean>;
	canRemoveSeat: (memberId: number) => boolean;
}

const UpdateSeatsContext = createContext<UpdateSeatsContextValue>();

export const useUpdateSeats = (): UpdateSeatsContextValue => {
	const [selectedSeats, setSelectedSeats] = createSignal<Set<number>>(new Set());
	const [hasInitialData, setHasInitialData] = createSignal(false);
	const queryClient = useQueryClient();

	// Получаем данные о команде
	const teamQuery = useQuery(() => TeamApi.getTeamInfoOptions());

	// Массив выбранных мест для API в формате UpgradeItem[]
	const upgradeItems = createMemo<UpgradeItem[]>(() => {
		return Array.from(selectedSeats()).map((companyAccountId) => ({
			company_account_id: companyAccountId,
			action: UpgradeAction.Delete,
			new_plan: null
		}));
	});

	// Получаем расчет цен от API
	// При первом открытии отправляем пустой массив, потом только если есть выбранные элементы
	const calculationQuery = useQuery(() => {
		const options = TeamApi.calculateUpgradeCostOptions(
			upgradeItems(),
			UpgradeWhen.FromNewBillingPeriod
		);

		// Включаем запрос если:
		// 1. Еще не получили начальные данные (первое открытие с пустым массивом)
		// 2. Есть выбранные элементы
		const shouldFetch = !hasInitialData() || upgradeItems().length > 0;

		return {
			...options,
			enabled: shouldFetch
		};
	});

	// Отслеживаем получение первых данных
	createEffect(() => {
		if (!hasInitialData() && calculationQuery.data) {
			setHasInitialData(true);
		}
	});

	// Analytics: Modal shown (only once)
	const [modalShownSent, setModalShownSent] = createSignal(false);
	createEffect(() => {
		const teamData = teamQuery.data;
		if (teamData && !modalShownSent()) {
			const currentSeats = teamData.team_member_total_amount || 0;
			const currentPrice = teamData.next_payment?.total_amount || 0;

			EventAnalytics.team.removeSeats.modalShown({
				seats_number_current: currentSeats,
				plan_price_current: currentPrice
			});
			setModalShownSent(true);
		}
	});

	// Мутация для удаления мест
	const removeSeatsMutation = useMutation(() => ({
		mutationKey: ["removeSeats"],
		mutationFn: async () => {
			const teamData = teamQuery.data;
			if (!teamData) {
				throw new Error("Team data not available");
			}

			if (selectedSeats().size === 0) {
				throw new Error("No seats selected");
			}

			const upgrades = Array.from(selectedSeats()).map((companyAccountId) => ({
				company_account_id: companyAccountId,
				action: SubscriptionAction.Delete
			}));

			const response = await TeamApi.manageSubscription({
				upgrades,
				when: "from_new_billing_period"
			});

			return response;
		},
		onSuccess: () => {
			// Analytics: Success
			EventAnalytics.team.removeSeats.success();

			showToastMessage({
				title: "Seats removed successfully",
				type: ToastMessageTypes.Success
			});
			queryClient.invalidateQueries({ queryKey: ["teamInfo"] });
			// Закрываем модал и очищаем выбор
			setSelectedSeats(new Set<number>());
			closeUpdateSeatsModal();
		},
		onError: (error) => {
			// Analytics: Error
			EventAnalytics.team.removeSeats.error({
				seats_to_remove: selectedSeats().size,
				error_message: getPublicMessageFromError(error) || "Unknown error"
			});

			showToastMessage({
				title: `Failed to remove seats: ${getPublicMessageFromError(error)}`,
				type: ToastMessageTypes.Error
			});
		}
	}));

	// Текущий пользователь (владелец - тот, кто не может быть удален)
	const currentUserId = () => getUserStore()?.id;
	const isOwner = (member: TeamMember) =>
		member.role === MemberRole.Owner ||
		(member.user_id !== null && member.user_id === currentUserId());

	// Получаем список членов команды и неиспользуемые места
	const teamMembers = () => teamQuery.data?.members || [];
	const totalSeats = () => teamQuery.data?.team_member_total_amount || 0;
	const usedSeats = () => teamQuery.data?.team_member_amount || 0;
	const unusedSeatsCount = () => totalSeats() - usedSeats();

	// Мемоизируем количество мест, которые можно удалить (не считая owner)
	const removableSeatsCount = createMemo(() => {
		const members = teamMembers();
		return members.filter((m) => !isOwner(m)).length;
	});

	// Проверяем, можно ли удалить это место
	const canRemoveSeat = (memberId: number) => {
		// Если уже выбрано, можно снять выбор
		if (selectedSeats().has(memberId)) {
			return true;
		}

		// Проверяем, что после добавления останется минимум 1 место (не считая owner)
		const wouldRemain = removableSeatsCount() - (selectedSeats().size + 1);

		// Минимум должно остаться 1 место
		return wouldRemain >= 1;
	};

	// Обработчик изменения чекбокса
	const handleCheckboxChange = (memberId: number) => {
		setSelectedSeats((prev: Set<number>) => {
			const newSet = new Set(prev);
			const isChecked = !newSet.has(memberId);

			if (newSet.has(memberId)) {
				newSet.delete(memberId);
			} else {
				newSet.add(memberId);
			}

			// Analytics: Member toggle
			const teamData = teamQuery.data;
			const calcData = calculationQuery.data;
			if (teamData && calcData) {
				const currentSeats = teamData.team_member_total_amount || 0;
				const newSeats = currentSeats - newSet.size;
				const currentPrice = calcData.current_monthly_cost || 0;
				const newPrice = calcData.new_monthly_cost || 0;
				const priceDiff = calcData.total_cost_change || 0;

				EventAnalytics.team.removeSeats.memberToggle({
					member_account_id: memberId,
					is_checked: isChecked,
					seats_number_current: currentSeats,
					seats_number_new: newSeats,
					seats_to_remove: newSet.size,
					plan_price_current: currentPrice,
					plan_price_new: newPrice,
					price_difference: priceDiff
				});
			}

			return newSet;
		});
	};

	// Количество выбранных мест для удаления
	const selectedCount = () => selectedSeats().size;

	// Данные из API
	const newTotalPrice = () => {
		if (selectedCount() === 0) return null;
		return calculationQuery.data?.new_monthly_cost || null;
	};
	const totalCostChange = () => {
		if (selectedCount() === 0) return null;
		return calculationQuery.data?.total_cost_change || null;
	};
	const isCalculating = () => calculationQuery.isFetching;

	return {
		// State
		selectedSeats,
		teamQuery,
		calculationQuery,

		// Computed
		teamMembers,
		totalSeats,
		usedSeats,
		unusedSeatsCount,
		selectedCount,
		newTotalPrice,
		totalCostChange,
		isCalculating,

		// Methods
		handleCheckboxChange,
		isOwner,
		handleConfirmRemoval: async () => {
			// Analytics: Confirm removal
			const teamData = teamQuery.data;
			const calcData = calculationQuery.data;
			if (teamData && calcData) {
				const currentSeats = teamData.team_member_total_amount || 0;
				const seatsToRemove = selectedSeats().size;
				const newSeats = currentSeats - seatsToRemove;
				const currentPrice = calcData.current_monthly_cost || 0;
				const newPrice = calcData.new_monthly_cost || 0;
				const priceDiff = calcData.total_cost_change || 0;
				const updateAt = teamData.next_payment?.payment_date || new Date().toISOString();

				EventAnalytics.team.removeSeats.confirm({
					seats_number_current: currentSeats,
					seats_number_new: newSeats,
					seats_to_remove: seatsToRemove,
					plan_price_current: currentPrice,
					plan_price_new: newPrice,
					price_difference: priceDiff,
					update_at: updateAt
				});
			}

			await removeSeatsMutation.mutateAsync();
		},
		isRemoving: () => removeSeatsMutation.isPending,
		canRemoveSeat
	};
};

export const useUpdateSeatsContext = () => {
	const context = useContext(UpdateSeatsContext);
	if (!context) {
		throw new Error("useUpdateSeatsContext must be used within UpdateSeatsProvider");
	}
	return context;
};

export { UpdateSeatsContext };
